import React from 'react';
import { formatMexicoDateTime as formatDate } from '../../dateUtils';
import { AlertTriangle, Clock, TrendingUp } from 'lucide-react';

const parseNumericTime = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

// Convierte tiempos en formato HH:MM[:SS] o horas decimales a segundos
const timeToSeconds = (timeValue) => {
  if (timeValue === null || timeValue === undefined || timeValue === '') return 0;

  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const parts = timeValue.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return (hours * 3600) + (minutes * 60) + seconds;
  }

  const numericHours = parseNumericTime(timeValue);
  return numericHours !== null ? numericHours * 3600 : 0;
};

const SLABurnedTicketsDomo = ({ tickets = [], cardClasses = '' }) => {
  // Validar que tickets sea un array
  const validTickets = Array.isArray(tickets) ? tickets : [];

  const ticketsValidos = validTickets.filter(t => 
    (
      t.Estado_de_solicitud === "Cerrado" ||
      t.Estado_de_solicitud === "Solucionado" ||
      t.Estado_de_solicitud === "Pendiente por usuario"
    )
    && t.Tipo_de_ticket !== "Incidente"
  );
  
  // Filtrar tickets con SLA quemado
  const burnedTickets = ticketsValidos.filter(t => {
    const responseSecs = timeToSeconds(t.Tiempo_de_respuesta_transcurrido);
    // El backend ahora envía SLA válidos, pero por si acaso validamos
    let threshold = timeToSeconds(t.Tiempo_de_respuesta_de_SLA);
    // Solo usar fallback si es realmente 0 (no debería pasar ya que el backend lo asigna)
    if (threshold === 0) {
      threshold = 1 * 60 * 60; // 1 hora por defecto
    }
    return responseSecs > threshold;
  });

  // Calcular cuánto excedió cada ticket
  const ticketsWithExcess = burnedTickets.map(ticket => {
    const responseSecs = timeToSeconds(ticket.Tiempo_de_respuesta_transcurrido);
    let threshold = timeToSeconds(ticket.Tiempo_de_respuesta_de_SLA);
    if (threshold === 0) {
      threshold = 1 * 60 * 60; // 1 hora por defecto
    }
    
    const excessSecs = responseSecs - threshold;
    const excessMins = Math.floor(excessSecs / 60);
    const excessHours = (excessSecs / 3600).toFixed(1);
    
    return {
      ...ticket,
      excessMins,
      excessHours,
      thresholdMins: Math.floor(threshold / 60)
    };
  }).sort((a, b) => b.excessMins - a.excessMins); // Ordenar por exceso mayor

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'Critica': return 'bg-red-500/20 text-red-400';
      case 'Alta': return 'bg-orange-500/20 text-orange-400';
      case 'Media': return 'bg-yellow-500/20 text-yellow-400';
      case 'Baja': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getExcessColor = (excessMins) => {
    if (excessMins > 120) return 'text-red-500 font-bold';
    if (excessMins > 60) return 'text-orange-500 font-semibold';
    return 'text-yellow-500';
  };

  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8 border-2 border-red-500/30`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-red-500" />
          Tickets con Incumplimiento de SLA de Primera Respuesta DOMO  ({burnedTickets.length})
        </h3>
        {burnedTickets.length > 0 && (
          <div className="bg-red-500/20 px-4 py-2 rounded-lg">
            <span className="text-red-400 font-semibold text-sm">⚠️ Requiere atención inmediata</span>
          </div>
        )}
      </div>

      {burnedTickets.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
            <Clock className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-lg font-semibold text-green-400">¡Excelente trabajo!</p>
          <p className="text-sm opacity-70 mt-2">No hay tickets con SLA quemado en este período</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-red-500/20">
                <th className="text-left py-3 px-4 font-semibold">ID</th>
                <th className="text-left py-3 px-4 font-semibold">Asunto</th>
                <th className="text-left py-3 px-4 font-semibold">Cuenta</th>
                <th className="text-left py-3 px-4 font-semibold">Prioridad</th>
                <th className="text-left py-3 px-4 font-semibold">Tipo</th>
                <th className="text-left py-3 px-4 font-semibold">Técnico</th>
                <th className="text-left py-3 px-4 font-semibold">SLA Objetivo</th>
                <th className="text-left py-3 px-4 font-semibold">Tiempo Real</th>
                <th className="text-left py-3 px-4 font-semibold">Exceso</th>
                <th className="text-left py-3 px-4 font-semibold">Creado</th>
              </tr>
            </thead>
            <tbody>
              {ticketsWithExcess.map((ticket, index) => (
                <tr 
                  key={index} 
                  className="border-b border-red-500/10 hover:bg-red-500/5 transition-colors duration-200"
                >
                  <td className="py-4 px-4">
                    <div className="font-bold text-blue-400">#{ticket.ID_Solicitud}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-medium">{ticket.Asunto}</div>
                    <div className="text-sm opacity-70">{ticket.Producto}</div>
                  </td>
                  <td className="py-4 px-4 font-medium">{ticket.Cuenta}</td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket.Prioridad)}`}>
                      {ticket.Prioridad}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm font-medium">{ticket.Tipo_de_ticket || 'Solicitud'}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-medium">{ticket.Tecnico}</div>
                    <div className="text-sm opacity-70">{ticket.Grupo_de_Soporte}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-400">
                        {ticket.thresholdMins} min
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm font-semibold text-red-400">
                      {ticket.Tiempo_de_respuesta_transcurrido || 'N/A'}h
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className={`w-4 h-4 ${getExcessColor(ticket.excessMins)}`} />
                      <span className={`text-sm font-bold ${getExcessColor(ticket.excessMins)}`}>
                        +{ticket.excessMins} min
                      </span>
                    </div>
                    <div className="text-xs opacity-70 mt-1">
                      ({ticket.excessHours}h de exceso)
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm font-medium">{formatDate(ticket.Hora_de_creacion)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {burnedTickets.length > 0 && (
        <div className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-400">Recomendación</p>
              <p className="text-sm opacity-70 mt-1">
                Estos tickets han excedido el tiempo de primera respuesta según su prioridad. 
                Se recomienda revisión inmediata para mejorar los KPIs del equipo.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SLABurnedTicketsDomo;